import React, { useState, useCallback, memo } from 'react';
import { Target, Plus, RefreshCw, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import GoalsEvolutionChart from './GoalsEvolutionChart';

// Memoized goal item to prevent unnecessary re-renders
const GoalItem = memo(function GoalItem({ 
  goal, 
  onEditGoal, 
  onGeneratePDF, 
  onRefreshGoal, 
  onToggleActive, 
  onDeleteGoal,
  onShowChart,
  t 
}) {
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className={`${t.inner} rounded-lg p-4`}>
      <div className="flex items-start justify-between">
        <div>
          <h4 className={`font-semibold ${t.text}`}>{goal.name}</h4>
          <p className={`text-sm ${t.textMut}`}>
            {formatDate(goal.period_start)} → {formatDate(goal.period_end)} • {goal.real_metrics?.days_count || 0} dias
          </p>
          <div className="flex items-center gap-4 mt-2">
            <div className="text-center">
              <p className={`text-xs ${t.textMut}`}>Eficiência Real</p>
              <p className="text-lg font-bold text-cyan-500">{goal.real_metrics?.efficiency?.toFixed(1) || 0}%</p>
            </div>
            <div className="text-2xl text-violet-500">→</div>
            <div className="text-center">
              <p className={`text-xs ${t.textMut}`}>Meta</p>
              <p className="text-lg font-bold text-violet-500">{goal.simulated_metrics?.efficiency?.toFixed(1) || 0}%</p>
            </div>
            <div className="text-center ml-4">
              <p className={`text-xs ${t.textMut}`}>Ganho</p>
              <p className="text-lg font-bold text-emerald-500">+{goal.simulated_metrics?.efficiency_gain?.toFixed(1) || 0}pp</p>
            </div>
            <div className="text-center ml-4">
              <p className={`text-xs ${t.textMut}`}>Tempo Poupado</p>
              <p className="text-lg font-bold text-amber-500">{Math.round((goal.simulated_metrics?.time_saved_total || 0) / 60)}h</p>
            </div>
            {goal.simulated_metrics?.estimated_extra_cuts > 0 && (
              <div className="text-center ml-4">
                <p className={`text-xs ${t.textMut}`}>Cortes Extra</p>
                <p className="text-lg font-bold text-green-500">+{goal.simulated_metrics.estimated_extra_cuts}</p>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <button
            onClick={() => onShowChart(goal)}
            className={`px-3 py-1.5 text-sm rounded-lg text-violet-500 bg-violet-500/10 flex items-center gap-1`}
            data-testid={`goal-chart-btn-${goal.id}`}
          >
            <TrendingUp className="w-3.5 h-3.5" />
            Evolução
          </button>
          <button
            onClick={() => onEditGoal(goal)}
            className={`px-3 py-1.5 text-sm rounded-lg text-blue-500 bg-blue-500/10`}
          >
            Editar
          </button>
          <button
            onClick={() => onGeneratePDF(goal)}
            className={`px-3 py-1.5 text-sm rounded-lg text-violet-500 bg-violet-500/10`}
          >
            PDF
          </button>
          <button
            onClick={() => onRefreshGoal(goal.id)}
            className={`px-3 py-1.5 text-sm rounded-lg ${t.hover}`}
          >
            Atualizar
          </button>
          <button
            onClick={() => onToggleActive(goal.id)}
            className={`px-3 py-1.5 text-sm rounded-lg ${goal.is_active ? 'text-emerald-500 bg-emerald-500/10' : 'text-gray-500 bg-gray-500/10'}`}
          >
            {goal.is_active ? 'Activa' : 'Inactiva'}
          </button>
          <button
            onClick={() => onDeleteGoal(goal.id)}
            className="px-3 py-1.5 text-sm rounded-lg text-red-500 bg-red-500/10"
          >
            Eliminar
          </button>
        </div>
      </div>

      {/* Parameters Info */}
      <div className={`mt-3 pt-3 border-t ${t.divider} flex flex-wrap gap-x-6 gap-y-2 text-xs`}>
        <span className={t.textMut}>
          Redução E.Marc: <b className="text-cyan-500">{goal.parameters?.between_markers_reduction || 0}%</b>
        </span>
        <span className={t.textMut}>
          Redução Parada: <b className="text-red-500">{goal.parameters?.machine_stopped_reduction || 0}%</b>
        </span>
        <span className={t.textMut}>
          Meta E.Marc: <b className={goal.simulated_metrics?.meets_bm_target ? 'text-emerald-500' : 'text-amber-500'}>
            ≤{goal.parameters?.target_between_markers_time || 2}min
          </b>
          {goal.simulated_metrics?.avg_between_markers_per_cut !== undefined && (
            <span className={t.textMut}> (atual: {goal.simulated_metrics.avg_between_markers_per_cut.toFixed(1)}min)</span>
          )}
        </span>
        <span className={t.textMut}>
          Meta Eficiência: <b className={goal.simulated_metrics?.meets_efficiency_target ? 'text-emerald-500' : 'text-amber-500'}>
            ≥{goal.parameters?.target_efficiency || 35}%
          </b>
          {goal.simulated_metrics?.efficiency !== undefined && (
            <span className={t.textMut}> (sim: {goal.simulated_metrics.efficiency.toFixed(1)}%)</span>
          )}
        </span>
        <span className={t.textMut}>
          Meta % Parada: <b className={goal.simulated_metrics?.meets_ms_percent_target ? 'text-emerald-500' : 'text-amber-500'}>
            ≤{goal.parameters?.target_machine_stopped_percent || 10}%
          </b>
          {goal.simulated_metrics?.machine_stopped_percent !== undefined && (
            <span className={t.textMut}> (sim: {goal.simulated_metrics.machine_stopped_percent.toFixed(1)}%)</span>
          )}
        </span>
      </div>
    </div>
  );
});

function GoalsManagement({ 
  goals, loadingGoals, onCreateGoal, onToggleActive, onDeleteGoal, onRefreshGoal,
  onEditGoal, onGeneratePDF,
  selectedGoalForChart, setSelectedGoalForChart, t 
}) {
  // Local state for chart - completely isolated from parent re-renders
  const [chartGoal, setChartGoal] = useState(null);
  
  const activeGoals = goals.filter(g => g.is_active);
  
  // Determine if dark mode (check if t.bg contains 'dark' or slate-900)
  const isDarkMode = t.bg?.includes('slate-9') || t.bg?.includes('dark') || false;
  
  // Handler to show chart - finds the goal and sets it locally
  const handleShowChart = useCallback((goal) => {
    // Create a shallow copy of the goal to avoid reference issues
    // Include both real_metrics and simulated_metrics which contain daily_data
    setChartGoal({
      id: goal.id,
      name: goal.name,
      parameters: goal.parameters ? { ...goal.parameters } : {},
      real_metrics: goal.real_metrics ? { ...goal.real_metrics } : {},
      simulated_metrics: goal.simulated_metrics ? { ...goal.simulated_metrics } : {}
    });
  }, []);
  
  // Handler to close chart
  const handleCloseChart = useCallback(() => {
    setChartGoal(null);
  }, []);

  return (
    <div className="space-y-6" data-testid="goals-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-violet-500/20">
            <Target className="w-6 h-6 text-violet-500" />
          </div>
          <div>
            <h2 className={`text-xl font-bold ${t.text}`}>Gestão de Metas</h2>
            <p className={`text-sm ${t.textMut}`}>
              {goals.length} meta{goals.length !== 1 ? 's' : ''} • {activeGoals.length} activa{activeGoals.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <Button onClick={onCreateGoal} className="bg-violet-500 hover:bg-violet-600 text-white">
          <Plus className="w-4 h-4 mr-2" />
          Nova Meta
        </Button>
      </div>

      {/* Evolution Chart - Rendered when a goal is selected */}
      {chartGoal && (
        <GoalsEvolutionChart 
          goal={chartGoal}
          onClose={handleCloseChart}
          isDarkMode={isDarkMode}
        />
      )}

      {loadingGoals ? (
        <div className={`${t.card} rounded-xl p-12 text-center`}>
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 text-violet-500" />
          <p className={t.textMut}>A carregar metas...</p>
        </div>
      ) : goals.length === 0 ? (
        <div className={`${t.card} rounded-xl p-12 text-center`}>
          <Target className="w-16 h-16 mx-auto mb-4 text-violet-500/30" />
          <h3 className={`text-lg font-semibold ${t.text} mb-2`}>Nenhuma meta criada</h3>
          <p className={`${t.textMut} mb-4`}>Crie metas baseadas em períodos de tempo para acompanhar a evolução</p>
          <Button onClick={onCreateGoal} className="bg-violet-500 hover:bg-violet-600 text-white">
            <Plus className="w-4 h-4 mr-2" />
            Criar Primeira Meta
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Goals List */}
          <div className={`${t.card} rounded-xl p-6`}>
            <h3 className={`text-lg font-bold ${t.text} mb-4`}>
              Metas ({goals.length})
            </h3>
            <div className="space-y-3">
              {goals.map((goal) => (
                <GoalItem
                  key={goal.id}
                  goal={goal}
                  onEditGoal={onEditGoal}
                  onGeneratePDF={onGeneratePDF}
                  onRefreshGoal={onRefreshGoal}
                  onToggleActive={onToggleActive}
                  onDeleteGoal={onDeleteGoal}
                  onShowChart={handleShowChart}
                  t={t}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GoalsManagement;
